import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { Disclosure, DisclosureButton } from "@headlessui/react";
import Constant from "../../../../utils/constant";
import SelectPageSize from "../../../shared/common/pageSizeDropdown";
import Pagination from "../../../shared/common/pagination";
import DeleteModal from "../../../shared/common/deleteModal";
import Loader from "../../../shared/loader/loader";
import AllowedTo from "../../../shared/common/rbac";
import pagePermissions from "../../../../utils/permissions";
import AccessDenied from "../../../shared/common/accessDenied";
import {
  AddIcon,
  EditIcon,
  DeleteIcon,
  CaretUpSolidIcon,
  CaretDownSolidIcon,
  CollapseDownIcon,
  CollapseUpIcon,
} from "../../../../assets/icons/index";
import {
  deletePermissionGroup,
  getAllPermissionGroups,
  getPermissionGroupAccessByGroupId,
  updatePermissionGroupAccess,
  deletePermissionGroupAccess,
} from "../../../../api/permissionGroup.service";
import { IPermissionGroup } from "../../../../model/interface/permissionGroup";
import { IPermissionGroupAccess } from "../../../../model/interface/permissionGroupAccess";
import { useConfirmation } from "../../../shared/confirmation/confirmationService";

const PermissionGroup: React.FC = () => {
  const defaultPageNumber = 1;
  const defaultPageSize = 10;
  const confirm = useConfirmation();
  const [pageNumber, setPageNumber] = useState(defaultPageNumber);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [permissionGroups, setPermissionGroups] = useState<IPermissionGroup[]>(
    []
  );
  const [groupPermissions, setGroupPermissions] = useState<
    IPermissionGroupAccess[]
  >([]);
  const [associatePermissionGroup, setAssociatePermissionGroup] =
    useState<number>(0);
  const [orderByColumn, setOrderByColumn] = useState("name");
  const [totalPages, setTotalPages] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [sortByDesc, setSortByDesc] = useState<boolean>(true);
  const [toggle, setToggle] = useState<number>();
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const navigate = useNavigate();
  const paginationRef: any = useRef(undefined);
  const pageSizeRef: any = useRef(undefined);

  const handleRedirect = (permissionGroupID?: number) => {
    navigate(
      permissionGroupID
        ? Constant.EDIT_PERMISSION_GROUP.replace(
            ":id",
            permissionGroupID.toString()
          )
        : Constant.ADD_PERMISSION_GROUP
    );
  };

  const handlePermissionGroupDelete = (permissionGroupId: number) => {
    setShowDeleteModal(true);
    setAssociatePermissionGroup(permissionGroupId);
  };

  const deleteRecordHandler = async () => {
    setLoading(true);
    if (associatePermissionGroup) {
      await deletePermissionGroup(associatePermissionGroup).then(() => {
        getPermissionGroupsData();
        setShowDeleteModal(false);
      });
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleSortBy = (column: string) => {
    setSortByDesc(!sortByDesc);
    setOrderByColumn(column);
  };

  async function getPermissionGroupsData(isReset?) {
    setLoading(true);

    getAllPermissionGroups(
      orderByColumn,
      sortByDesc ? "ASC" : "DESC",
      pageSize,
      pageNumber
    ).then((response) => {
      setPermissionGroups(response.data.data);
      setTotalPages(
        Math.ceil(
          response.data.totalRecords / (isReset ? defaultPageSize : pageSize)
        )
      );
      setTotalRecords(response.data.totalRecords);
      setLoading(false);
    });
  }

  useEffect(() => {
    if (isLoaded) {
      getPermissionGroupsData();
    }
    setToggle(undefined);
  }, [pageNumber]);

  useEffect(() => {
    if (pageNumber === 1) {
      getPermissionGroupsData();

      setIsLoaded(true);
    } else {
      paginationRef?.current?.onReset();
    }
    setToggle(undefined);
  }, [pageSize]);

  useEffect(() => {
    if (isLoaded) {
      getPermissionGroupsData();
    }
  }, [sortByDesc]);

  const getPermissionGroupAccess = (id) => {
    getPermissionGroupAccessByGroupId(id).then((res) => {
      setGroupPermissions(res.data);
    });
  };

  const handlePermissionGroupsPermissions = async (id, index?, type?) => {
    setLoading(true);
    if (type === "show") {
      const response = getPermissionGroupAccessByGroupId(id);
      const data = (await response).data;

      setGroupPermissions(data);
      setToggle(index);
    } else {
      setToggle(undefined);
    }
    setLoading(false);
  };

  const saveGroupAccessPermission = (
    permissionGroupAccess: IPermissionGroupAccess,
    e: any,
    permissionType: string,
    index: number
  ) => {
    const permissionGroupAccessObject: IPermissionGroupAccess = {
      name: permissionGroupAccess.name,
      permissionGroupAccessId: permissionGroupAccess.permissionGroupAccessId,
      permissionGroupId: permissionGroupAccess.permissionGroupId,
      permissionId: permissionGroupAccess.permissionId,
      view:
        permissionType === "VIEW"
          ? e.target.checked
          : permissionGroupAccess.view,
      create:
        permissionType === "CREATE"
          ? e.target.checked
          : permissionGroupAccess.create,
      modify:
        permissionType === "MODIFY"
          ? e.target.checked
          : permissionGroupAccess.modify,
      delete:
        permissionType === "DELETE"
          ? e.target.checked
          : permissionGroupAccess.delete,
      upgrade:
        permissionType === "UPGRADE"
          ? e.target.checked
          : permissionGroupAccess.upgrade,
    };

    if (
      permissionGroupAccessObject.create ||
      permissionGroupAccessObject.delete ||
      permissionGroupAccessObject.modify
    ) {
      permissionGroupAccessObject.view = true;
    }

    if (permissionType === "VIEW" && !e.target.checked) {
      permissionGroupAccessObject.create = false;
      permissionGroupAccessObject.modify = false;
      permissionGroupAccessObject.delete = false;
      permissionGroupAccessObject.view = false;
    }

    updatePermissionGroupAccess(permissionGroupAccessObject)
      .then(() => {
        getPermissionGroupAccess(permissionGroupAccess.permissionGroupId);
        toast.success("Group Permission Access Updated");
      })
      .catch(() => {
        toast.error("Group Permission Access Update failed !!");
      });
  };

  const handleDeletePermissionGroupAccess = (
    id: number,
    groupId: number,
    index: number
  ) => {
    confirm({
      variant: "danger",
      title: "Please Confirm",
      description: "Are you sure you want to delete permission group",
    }).then((d) => {
      deletePermissionGroupAccess(id)
        .then((res) => {
          toast.success("Group permission access deleted successfully !!");
          getPermissionGroupAccess(groupId);
        })
        .catch((err) => {
          toast.error("Error while deleting group permission access");
        });
    });
  };

  return (
    <AllowedTo
      perform={pagePermissions.permission_groups_view}
      no={() => <AccessDenied />}
    >
      {loading && <Loader />}

      {showDeleteModal && (
        <DeleteModal
          closeDeleteModal={closeDeleteModal}
          deleteRecord={deleteRecordHandler}
        />
      )}
      <div className="flex-1 overflow-auto">
        <div className=" h-auto min-h-half w-full rounded-bl-3xl bg-site-teal px-8  py-8 pt-0 pb-14 mt-7 ">
          <div className="relative  py-1">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-light text-gray-900">
                Permission Group
              </h3>
              <AllowedTo perform={pagePermissions.permission_groups_create}>
                <button
                  type="button"
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 rounded-md shadow-xs text-white dark:text-white bg-primary-color hover:bg-secondary-color focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color hover:shadow-lg transsition ease-in-out duration-300"
                  onClick={() => handleRedirect()}
                >
                  <AddIcon className="w-6"></AddIcon>
                  Add Permission Group
                </button>
              </AllowedTo>
            </div>
            <div id="dataContainer" className="mt-4">
              <div className="flex flex-col mt-1">
                <div className="-my-2  overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div className=" shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-primary-color">
                          <tr>
                            <th
                              scope="col"
                              className="px-3 py-2 pl-6 font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex gap-2">
                                Name
                                <span className="cursor-pointer">
                                  {!sortByDesc && orderByColumn === "name" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSortBy("name")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSortBy("name")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>

                            <th
                              scope="col"
                              className="w-20 px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider"
                            >
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {permissionGroups.map((permissionGroup, index) => (
                            <Disclosure key={index}>
                              {({ open }) => (
                                <>
                                  <tr
                                    key={index}
                                    className={
                                      index % 2 === 0
                                        ? "bg-white"
                                        : "bg-gray-100"
                                    }
                                  >
                                    <td className="px-3 py-2 pl-6  text-sm text-left font-normal text-gray-500 relative">
                                      <div className="flex ...">
                                        <div className="mt-2">
                                          <DisclosureButton className="">
                                            {index !== toggle && (
                                              <CollapseDownIcon
                                                key={index}
                                                className={`w-5 h-5 text-gray-500`}
                                                onClick={(e) => {
                                                  handlePermissionGroupsPermissions(
                                                    permissionGroup?.permissionGroupId,
                                                    index,
                                                    "show"
                                                  );
                                                }}
                                              />
                                            )}

                                            {index === toggle && (
                                              <CollapseUpIcon
                                                key={index}
                                                className={`w-5 h-5 text-gray-500`}
                                                onClick={() => {
                                                  handlePermissionGroupsPermissions(
                                                    permissionGroup?.permissionGroupId,
                                                    index,
                                                    "hide"
                                                  );
                                                }}
                                              />
                                            )}
                                          </DisclosureButton>
                                        </div>
                                        <div className="mt-2 ml-2">
                                          <a
                                            className="cursor-pointer font-medium  text-indigo-600 hover:text-indigo-500 hover:underline"
                                            onClick={() =>
                                              handleRedirect(
                                                permissionGroup.permissionGroupId
                                              )
                                            }
                                            href={Constant.EDIT_PERMISSION_GROUP.replace(
                                              ":id",
                                              permissionGroup.permissionGroupId.toString()
                                            )}
                                          >
                                            {permissionGroup.name}
                                          </a>
                                        </div>
                                      </div>
                                    </td>

                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-center font-normal text-gray-700">
                                      <div className="flex gap-3 justify-center">
                                        <AllowedTo
                                          perform={
                                            pagePermissions.permission_groups_modify
                                          }
                                        >
                                          <EditIcon
                                            onClick={() =>
                                              handleRedirect(
                                                permissionGroup.permissionGroupId
                                              )
                                            }
                                            className="w-4 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300"
                                          />
                                        </AllowedTo>

                                        <AllowedTo
                                          perform={
                                            pagePermissions.permission_groups_delete
                                          }
                                        >
                                          <DeleteIcon
                                            onClick={() =>
                                              handlePermissionGroupDelete(
                                                permissionGroup.permissionGroupId
                                              )
                                            }
                                            className="w-4 text-xs font-light cursor-pointer hover:text-red-500 transaction ease-in-out duration-300"
                                          />
                                        </AllowedTo>
                                      </div>
                                    </td>
                                  </tr>
                                  <tr
                                    key={index}
                                    className={
                                      index % 2 === 0
                                        ? "bg-white"
                                        : "bg-gray-100"
                                    }
                                  >
                                    <td colSpan={4} className="relative ">
                                      <div
                                        className={`${
                                          index !== toggle ? "hidden" : ""
                                        } pl-12 pr-12 px-4 pb-2 text-sm text-gray-500`}
                                      >
                                        <div className="shadow border rounded-lg px-3 py-3">
                                          <table className="min-w-full">
                                            <thead>
                                              <tr className="bg-gray-50">
                                                <th
                                                  scope="col"
                                                  className="inline-flex items-center gap-2 px-3 py-2 pl-6 font-semibold text-xs text-left text-gray-500 tracking-wider"
                                                >
                                                  <span className="flex gap-2">
                                                    Permission Name
                                                  </span>
                                                </th>

                                                <th
                                                  scope="col"
                                                  className="px-3 py-2 pl-6 font-semibold text-xs text-left text-gray-500 tracking-wider"
                                                >
                                                  <span className="flex gap-2">
                                                    View
                                                  </span>
                                                </th>
                                                <th
                                                  scope="col"
                                                  className="px-3 py-2 pl-6 font-semibold text-xs text-left text-gray-500 tracking-wider"
                                                >
                                                  <span className="flex gap-2">
                                                    Create
                                                  </span>
                                                </th>
                                                <th
                                                  scope="col"
                                                  className="px-3 py-2 pl-6 font-semibold text-xs text-left text-gray-500 tracking-wider"
                                                >
                                                  <span className="flex gap-2">
                                                    Modify
                                                  </span>
                                                </th>
                                                <th
                                                  scope="col"
                                                  className="px-3 py-2 pl-6 font-semibold text-xs text-left text-gray-500 tracking-wider"
                                                >
                                                  <span className="flex gap-2">
                                                    Delete
                                                  </span>
                                                </th>

                                                <th
                                                  scope="col"
                                                  className="px-3 py-2 pl-6 font-semibold text-xs text-left text-gray-500 tracking-wider"
                                                >
                                                  <span className="flex gap-2">
                                                    Upgrade Required
                                                  </span>
                                                </th>

                                                <th
                                                  scope="col"
                                                  className="px-3 py-2 pl-6 font-semibold text-xs text-left text-gray-500 tracking-wider"
                                                >
                                                  Action
                                                </th>
                                              </tr>
                                            </thead>
                                            <tbody className="divide-y">
                                              {groupPermissions.map(
                                                (permission, index) => (
                                                  <tr
                                                    key={index}
                                                    className={"bg-white"}
                                                  >
                                                    <td className="px-3 py-2 pl-6 whitespace-nowrap text-sm text-left font-normal  text-gray-500">
                                                      {permission.name}
                                                    </td>
                                                    <td className="px-3 py-2 pl-6 text-sm font-normal text-gray-500">
                                                      <div className="flex justify-start">
                                                        <input
                                                          onChange={(e) => {
                                                            saveGroupAccessPermission(
                                                              permission,
                                                              e,
                                                              "VIEW",
                                                              index
                                                            );
                                                          }}
                                                          name="viewAccess"
                                                          type="checkbox"
                                                          className="cursor-pointer"
                                                          checked={
                                                            permission.view
                                                          }
                                                        />
                                                      </div>
                                                    </td>
                                                    <td className="px-3 py-2 pl-6 text-sm font-normal text-gray-500">
                                                      <div className="flex justify-start">
                                                        <input
                                                          onChange={(e) => {
                                                            saveGroupAccessPermission(
                                                              permission,
                                                              e,
                                                              "CREATE",
                                                              index
                                                            );
                                                          }}
                                                          name="createAccess"
                                                          type="checkbox"
                                                          className="cursor-pointer"
                                                          checked={
                                                            permission.create
                                                          }
                                                        />
                                                      </div>
                                                    </td>
                                                    <td className="px-3 py-2 pl-6 text-sm font-normal text-gray-500">
                                                      <div className="flex justify-start">
                                                        <input
                                                          onChange={(e) => {
                                                            saveGroupAccessPermission(
                                                              permission,
                                                              e,
                                                              "MODIFY",
                                                              index
                                                            );
                                                          }}
                                                          name="modifyAccess"
                                                          type="checkbox"
                                                          className="cursor-pointer"
                                                          checked={
                                                            permission.modify
                                                          }
                                                        />
                                                      </div>
                                                    </td>
                                                    <td className="px-3 py-2 pl-6 text-sm font-normal text-gray-500">
                                                      <div className="flex justify-start">
                                                        <input
                                                          onChange={(e) => {
                                                            saveGroupAccessPermission(
                                                              permission,
                                                              e,
                                                              "DELETE",
                                                              index
                                                            );
                                                          }}
                                                          name="deleteAccess"
                                                          className="cursor-pointer"
                                                          type="checkbox"
                                                          checked={
                                                            permission.delete
                                                          }
                                                        />
                                                      </div>
                                                    </td>
                                                    <td className="px-3 py-2 pl-6 text-sm font-normal text-gray-500">
                                                      <div className="flex justify-start">
                                                        <input
                                                          onChange={(e) => {
                                                            saveGroupAccessPermission(
                                                              permission,
                                                              e,
                                                              "UPGRADE",
                                                              index
                                                            );
                                                          }}
                                                          name="upgradeAccess"
                                                          className="cursor-pointer"
                                                          type="checkbox"
                                                          checked={
                                                            permission.upgrade
                                                          }
                                                        />
                                                      </div>
                                                    </td>
                                                    <td className="px-3 py-2  pl-6 whitespace-nowrap text-sm font-normal text-gray-700">
                                                      <div className="flex gap-3 justify-start">
                                                        <AllowedTo
                                                          perform={
                                                            pagePermissions.permission_groups_delete
                                                          }
                                                        >
                                                          <DeleteIcon
                                                            onClick={() =>
                                                              handleDeletePermissionGroupAccess(
                                                                permission.permissionGroupAccessId!,
                                                                permission.permissionGroupId!,
                                                                index
                                                              )
                                                            }
                                                            className="w-4 text-xs font-light cursor-pointer hover:text-red-500 transaction ease-in-out duration-300"
                                                          />
                                                        </AllowedTo>
                                                      </div>
                                                    </td>
                                                  </tr>
                                                )
                                              )}
                                            </tbody>
                                          </table>
                                        </div>
                                        {groupPermissions.length === 0 && (
                                          <p className="p-2">
                                            No Permissions added
                                          </p>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                </>
                              )}
                            </Disclosure>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  {permissionGroups.length !== 0 ? (
                    <>
                      <Pagination
                        pages={totalPages}
                        setPageNumber={setPageNumber}
                        ref={paginationRef}
                      />
                      <span>
                        <span className="text-sm pr-4 ml-auto text-left-700">
                          Showing {pageNumber} to{" "}
                          {totalRecords < pageSize ? totalRecords : pageSize} of{" "}
                          {totalRecords} results
                        </span>

                        <SelectPageSize
                          setPageSize={setPageSize}
                          type={"second"}
                          ref={pageSizeRef}
                        />
                      </span>
                    </>
                  ) : (
                    <p className="p-2"></p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AllowedTo>
  );
};

export default PermissionGroup;
